import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material'
import { observer } from 'mobx-react-lite'

import {
  AcContainer,
  AcIcon,
  AcImage,
  AcTitle,
  AcTypography,
} from '@components'
import { ICONS } from '@constants'
import {
  ISingleContentBlockAccordion,
  ISingleContentBlockImage,
} from '@typings'
import { highlightHtmlContent } from '@utilities/helpers/highlight-html-content'

import styles from './ac-accordion.module.scss'

interface IAcAccordion extends ISingleContentBlockAccordion {
  hideTitle?: boolean
  home?: boolean
  blockId?: number
}

export const AcAccordion = observer(
  ({
    title,
    description,
    hideTitle,
    rows,
    home = false,
    blockId,
  }: IAcAccordion) => {
    const [searchParams] = useSearchParams()
    const [expandedPanels, setExpandedPanels] = useState<Set<number>>(new Set())
    const accordionRef = useRef<HTMLDivElement>(null)
    const hasScrolled = useRef(false)
    const hasProcessedUrlParams = useRef(false)

    // Capture initial URL params on first render to survive StrictMode double-mount
    const initialUrlParams = useRef({
      accordion: new URLSearchParams(window.location.search).get('accordion'),
      block: new URLSearchParams(window.location.search).get('block'),
      highlight: new URLSearchParams(window.location.search).get('highlight'),
    })

    // Use live searchParams for highlight (allows updates), but initial params for expansion logic
    const highlightTerm =
      searchParams.get('highlight') || initialUrlParams.current.highlight || ''

    useEffect(() => {
      // Use initial URL params to avoid StrictMode issues
      const accordionParam = initialUrlParams.current.accordion
      const blockParam = initialUrlParams.current.block

      // Only process URL params once per component lifecycle
      if (hasProcessedUrlParams.current) {
        return
      }

      // Auto-expand accordion if this block matches the URL parameter
      if (blockParam && blockId && parseInt(blockParam) === blockId) {
        hasProcessedUrlParams.current = true

        // If accordion param is provided, expand only that specific accordion item
        // Otherwise, expand all accordion items in this block
        if (accordionParam) {
          const accordionId = parseInt(accordionParam)
          // Find the row index that matches the accordion ID
          const rowIndex = rows.findIndex((row: any) => row.id === accordionId)

          if (rowIndex !== -1) {
            setExpandedPanels(new Set([rowIndex]))
          } else {
            // If no matching row found by ID, expand all
            const allIndices = new Set(rows.map((_, index) => index))
            setExpandedPanels(allIndices)
          }
        } else {
          // Expand all accordion items in this block
          const allIndices = new Set(rows.map((_, index) => index))
          setExpandedPanels(allIndices)
        }

        // Scroll to this accordion after a brief delay to ensure rendering
        if (!hasScrolled.current) {
          setTimeout(() => {
            if (accordionRef.current) {
              accordionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
              hasScrolled.current = true
            }
          }, 300)
        }
      }
    }, [blockId, rows])

    const handleAccordionChange =
      (index: number) =>
      (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanels(prev => {
          const newSet = new Set(prev)
          if (isExpanded) {
            newSet.add(index)
          } else {
            newSet.delete(index)
          }
          return newSet
        })
      }

    const renderAccordions = useMemo(() => {
      return rows.map((row, index) => (
        <Accordion
          key={index}
          expanded={expandedPanels.has(index)}
          onChange={handleAccordionChange(index)}>
          <AccordionSummary
            expandIcon={<AcIcon icon={ICONS.ADD} />}
            aria-controls={`panel${index}-content`}
            id={`panel${index}-header`}>
            <AcTypography
              weight="bold"
              mb={0}>
              {row.title}
            </AcTypography>
          </AccordionSummary>
          <AccordionDetails>
            <AcTypography html>
              {highlightTerm && row.content
                ? highlightHtmlContent({
                    searchTerm: highlightTerm,
                    content: row.content,
                  })
                : row.content}
            </AcTypography>
            {row.image && (
              <AcImage {...(row.image as ISingleContentBlockImage)} />
            )}
          </AccordionDetails>
        </Accordion>
      ))
    }, [rows, expandedPanels, highlightTerm])

    const renderContent = useMemo(() => {
      return (
        <div ref={accordionRef}>
          {!hideTitle && (
            <Box>
              <AcTitle
                level={2}
                color="primary">
                {title}
              </AcTitle>
              <AcTypography>{description}</AcTypography>
            </Box>
          )}
          <Box>{renderAccordions}</Box>
        </div>
      )
    }, [hideTitle, title, description, renderAccordions])

    if (home) {
      return (
        <Box className={styles['ac-accordion--home']}>
          <AcContainer maxWidth={782}>
            <Box className={styles['ac-accordion__content']}>
              <AcTitle
                level={2}
                color="primary">
                {title}
              </AcTitle>
              <AcTypography>{description}</AcTypography>
            </Box>
            <Box>{renderAccordions}</Box>
          </AcContainer>
        </Box>
      )
    }

    return renderContent
  },
)
